import {
  PREVIEW_QUOTE_CONFIRMATION_PATH,
  QUOTE_CONFIRMATION_PATH
} from '@common/utils'
import { MolsonAppBar, MolsonBox, MolsonButton } from '@components/reusable'
import { Box, useMediaQuery } from '@mui/material'
import { useGetCulture } from '@shared-queries'
import { useEffect, useState } from 'react'
import Avatar from 'react-avatar'
import { useLocation, useNavigate } from 'react-router-dom'
import { i18n } from '@/i18n/config'
import { useAuthStore } from '@/stores/authStore'
import { molsonUseTheme } from '@/theme/customMuiExports'
import { isCultureSwitcherFlagEnabled } from '@/utils/functions/feature-flags'
import { CultureSwitcher } from './CultureSwitcher'
import { Links } from './Links'
import { getCultureLanguage } from './utils'

type Culture = 'en-GB' | 'en-US'

export const Navbar = () => {
  const {
    auth,
    isAuthenticated,
    logoutUser,
    isLoading: isAuthLoading
  } = useAuthStore()

  const [currentCulture, setCurrentCulture] = useState<Culture>(() => {
    const stored = localStorage.getItem('preferredRegion')
    return (stored as Culture) ?? 'en-GB'
  })

  const { data: cultureData } = useGetCulture()

  // Set initial i18n language based on stored culture
  useEffect(() => {
    const i18nLanguage = getCultureLanguage(currentCulture)
    i18n.changeLanguage(i18nLanguage)
  }, [currentCulture])

  // Sync culture from API response
  useEffect(() => {
    if (cultureData?.culture?.currentCulture) {
      const apiCulture = cultureData?.culture?.currentCulture as Culture
      if (apiCulture.trim().length > 0) {
        setCurrentCulture(apiCulture)
        const i18nLanguage = getCultureLanguage(apiCulture)
        i18n.changeLanguage(i18nLanguage)
      }
    }
  }, [cultureData])

  const handleCultureChange = (newCulture: Culture) => {
    setCurrentCulture(newCulture)
  }

  const location = useLocation()

  const isQuoteConfirmation =
    location.pathname === QUOTE_CONFIRMATION_PATH ||
    location.pathname === PREVIEW_QUOTE_CONFIRMATION_PATH

  const name = auth?.user?.profile.preferred_username

  const navigate = useNavigate()
  const theme = molsonUseTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isAuthLoading) {
    return <MolsonAppBar placement="top" />
  }

  return (
    <MolsonAppBar placement="top">
      <MolsonBox
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'space-between',
          margin: '0 auto',
          maxWidth: '1440px',
          padding: isMobile ? '12px 8px 12px 16px' : '0 32px',
          width: '100%'
        }}
      >
        <MolsonBox sx={{ alignItems: 'center', display: 'flex', gap: '28px' }}>
          <Box
            onClick={() =>
              isAuthenticated && !isQuoteConfirmation && navigate('/')
            }
            sx={{
              cursor:
                isAuthenticated && !isQuoteConfirmation ? 'pointer' : 'auto'
            }}
            component="img"
            src={`${import.meta.env.NX_BLOB_STORAGE_URL}/assets/login_page_logo.svg`}
          />
          {isAuthenticated && !isQuoteConfirmation && <Links />}
        </MolsonBox>
        {isAuthenticated && !isQuoteConfirmation && (
          <MolsonBox display="flex" gap="10px">
            {isCultureSwitcherFlagEnabled() && (
              <CultureSwitcher
                currentCulture={currentCulture}
                onCultureChange={handleCultureChange}
              />
            )}
            {!isMobile && <Avatar name={name} round={true} size="40" />}
            <MolsonButton onClick={logoutUser} variant="contained">
              Logout
            </MolsonButton>
          </MolsonBox>
        )}
      </MolsonBox>
    </MolsonAppBar>
  )
}
